import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TextField, IconButton, InputAdornment, makeStyles, Chip, Popper, Paper, List, ListItem, ListItemText, ClickAwayListener } from "@material-ui/core";
import { Close, Search } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: 0
  },
  inputRoot: {
    paddingRight: 0,
    marginBottom: 0,
    flexWrap: "wrap",
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  chip: {
    margin: theme.spacing(0.5)
  },
  input: {
    flex: 1,
    minWidth: 120
  },
  popper: {
    zIndex: 1200
  },
  listItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  selectedListItem: {
    backgroundColor: theme.palette.action.selected
  }
}));

const ChipSearchTextField = ({
  handleSearch,
  value: inputValue = "",
  fullWidth = false,
  placeholder,
  allowedColumns = [],
  initialFilters = []
}) => {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const classes = useStyles();
  const inputRef = useRef(null);
  const anchorEl = useRef(null);
  const isInitialMount = useRef(true);

  const parseFilters = (text) => {
    const filterRegex = /(\w+)\s*:\s*([^,]+)/g;
    const parsed = [];
    let match;

    while ((match = filterRegex.exec(text)) !== null) {
      const column = match[1].trim();
      const value = match[2].trim();

      // Validate column against allowedColumns if provided
      if (allowedColumns.length === 0 || allowedColumns.includes(column)) {
        parsed.push({ column, value });
      }
    }

    return parsed;
  };

  const getColumnSuggestions = (text) => {
    if (allowedColumns.length === 0) return [];

    // Check if we're currently typing a column name (text without colon or before colon)
    const lastSegment = text.split(',').pop().trim();

    // If there's already a colon, don't show suggestions
    if (lastSegment.includes(':')) {
      const parts = lastSegment.split(':');
      if (parts.length > 1 && parts[1].trim().length > 0) {
        return [];
      }
    }

    // Get the partial column name
    const partialColumn = lastSegment.toLowerCase();

    // If empty, return all columns
    if (partialColumn === '') {
      return allowedColumns;
    }

    // Filter columns that start with the partial input
    return allowedColumns.filter(col =>
      col.toLowerCase().startsWith(partialColumn)
    );
  };

  const applySuggestion = (column) => {
    const lastCommaIndex = inputText.lastIndexOf(',');
    const baseText = lastCommaIndex >= 0 ? inputText.substring(0, lastCommaIndex + 1) + ' ' : '';
    const newText = baseText + column + ': ';
    setInputText(newText);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(0);

    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getPlaceholderText = () => {
    if (placeholder) return placeholder;
    if (allowedColumns.length > 0) {
      return `${t("search")} (${allowedColumns.join(", ")})`;
    }
    return t("search");
  };

  const debounce = (execute, delay) => {
    let timeout;

    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => execute(...args), delay);
    };
  };

  const debounceSuggest = useCallback(
    debounce(async (filterArray) => {
      await handleSearch(filterArray);
    }, 1000), [handleSearch]
  );

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);

    // Get column suggestions
    const columnSuggestions = getColumnSuggestions(text);
    setSuggestions(columnSuggestions);
    setShowSuggestions(columnSuggestions.length > 0);
    setSelectedSuggestionIndex(0);
  };

  const handleInputFocus = () => {
    // Show all available columns when field is focused
    if (inputText.trim() === '' && allowedColumns.length > 0) {
      const columnSuggestions = getColumnSuggestions('');
      setSuggestions(columnSuggestions);
      setShowSuggestions(true);
      setSelectedSuggestionIndex(0);
    }
  };

  const handleInputBlur = () => {
    // Use setTimeout to allow other click events to process first
    setTimeout(() => {
      // Clear input text if it doesn't contain valid filter pattern
      if (inputText.trim() !== '') {
        const parsed = parseFilters(inputText);
        if (parsed.length === 0) {
          // No valid filter pattern found, clear the text
          setInputText('');
        }
      }
    }, 200);
  };

  const handleKeyDown = (e) => {
    // Handle autocomplete navigation
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        return;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        return;
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        applySuggestion(suggestions[selectedSuggestionIndex]);
        return;
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
        return;
      }
    }

    // On Enter, parse and create chips
    if (e.key === "Enter") {
      e.preventDefault();
      const parsed = parseFilters(inputText);
      if (parsed.length > 0) {
        setFilters(prev => {
          // Merge with existing filters, avoiding duplicates
          const newFilters = [...prev];
          parsed.forEach(newFilter => {
            const exists = newFilters.some(f => f.column === newFilter.column && f.value === newFilter.value);
            if (!exists) {
              newFilters.push(newFilter);
            }
          });
          return newFilters;
        });
        setInputText("");
        setShowSuggestions(false);
      }
    } else if (e.key === "Backspace" && inputText === "" && filters.length > 0) {
      // Remove last chip on backspace when input is empty
      e.preventDefault();
      setFilters(prev => prev.slice(0, -1));
    }
  };

  const handleDeleteChip = (index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setFilters([]);
    setInputText("");
    handleSearch([]);
  };

  const handleClickAway = (event) => {
    // Don't close if clicking inside the TextField area
    if (anchorEl.current && anchorEl.current.contains(event.target)) {
      return;
    }

    setShowSuggestions(false);
  };

  useEffect(() => {
    // Skip the initial call when loading filters from URL to prevent refetch
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    debounceSuggest(filters);
  }, [filters]);

  return (
    <>
      <div ref={anchorEl}>
        <TextField
          placeholder={filters.length === 0 ? getPlaceholderText() : ""}
          value={inputText}
          variant="outlined"
          fullWidth={fullWidth}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          inputRef={inputRef}
          classes={{
            root: classes.field
          }}
          InputProps={{
            className: classes.inputRoot,
            startAdornment: filters.length > 0 ? (
              filters.map((filter, index) => (
                <Chip
                  key={`${filter.column}-${filter.value}-${index}`}
                  label={`${filter.column}: ${filter.value}`}
                  onDelete={() => handleDeleteChip(index)}
                  className={classes.chip}
                  size="small"
                  color="primary"
                />
              ))
            ) : null,
            endAdornment: (
              <InputAdornment position="end">
                {(inputText || filters.length > 0)
                  ? (
                    <IconButton
                      onClick={handleClearAll}
                    >
                      <Close />
                    </IconButton>
                  )
                  : (
                    <IconButton>
                      <Search />
                    </IconButton>
                  )}
              </InputAdornment>
            ),
            classes: {
              input: classes.input
            }
          }}
        />
      </div>

      {showSuggestions && (
        <Popper
          open={true}
          anchorEl={anchorEl.current}
          placement="bottom-start"
          className={classes.popper}
          style={{ width: anchorEl.current ? anchorEl.current.clientWidth : undefined }}
        >
          <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseUp" touchEvent="onTouchEnd">
            <Paper elevation={8}>
              <List dense>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={suggestion}
                    className={`${classes.listItem} ${index === selectedSuggestionIndex ? classes.selectedListItem : ''}`}
                    onClick={() => applySuggestion(suggestion)}
                  >
                    <ListItemText primary={`${suggestion}:`} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default ChipSearchTextField;
